import { ewent } from "../../reactive";
import { Log } from "../../util/Log";
import { LifetimeManager } from "../../lifetime/LifetimeManager";
import { FakeLodash } from "../../coreutil/FakeLodash";
export class AbstractCaptureService {
    constructor() {
        this.last_checked_tick = undefined;
        this.interests = [];
        this.raw_last_capture = undefined;
        this._currentInterest = null;
    }
    subscribe(token) {
        const real_token = (() => {
            if (token instanceof AbstractCaptureService.InterestToken)
                return token;
            return new class extends AbstractCaptureService.InterestToken {
                handle(value) {
                    token.handle?.(value);
                }
                isPaused() {
                    return token.paused?.() ?? false;
                }
                options(time) {
                    return token.options(time);
                }
                isOneTime() {
                    if (token.isOneTime)
                        return token.isOneTime();
                    return false;
                }
            };
        })();
        this.interests.push(real_token);
        real_token.onKilled(() => {
            const i = this.interests.indexOf(real_token);
            if (i >= 0)
                this.interests.splice(i, 1);
        });
        real_token.lifetime_manager.bind(...real_token.setupHandlers(this));
        return real_token;
    }
    transformValueForNotification(options, raw_value) {
        return raw_value.value;
    }
    doIfAnyInterest(time, f) {
        if (time.tick == this.last_checked_tick?.tick)
            return; // Skip if the tick was already done
        this.last_checked_tick = time;
        const interested_in_this_tick = this.getInterests(time);
        if (interested_in_this_tick.length > 0) {
            const raw_value = f(interested_in_this_tick.map(t => t.options));
            if (raw_value === undefined)
                return;
            const value = this.raw_last_capture = {
                time: time,
                value: raw_value,
                options: null
            };
            interested_in_this_tick.forEach(interest => {
                interest.token.notify({
                    time: time,
                    value: this.transformValueForNotification(interest.options, value),
                    options: interest.options
                });
            });
        }
    }
    getInterests(time) {
        if (!this._currentInterest || this._currentInterest.time.tick != time.tick) {
            this._currentInterest = {
                time: time,
                interests: this.interests
                    .filter(t => !t.isPaused() && t.isAlive())
                    .map(t => ({ options: t.options(time), token: t }))
                    .filter(t => t.options && t.options.interval.matches(time))
            };
        }
        return this._currentInterest.interests;
    }
    captureOnce(options) {
        return new Promise(resolve => {
            this.subscribe({
                options: () => ({
                    ...options.options,
                    interval: CaptureInterval.level(0)
                }),
                handle: (value) => resolve(value),
                isOneTime: () => true
            });
        });
    }
}
(function (AbstractCaptureService) {
    var log = Log.log;
    class InterestToken {
        constructor() {
            this.lifetime_manager = new LifetimeManager();
            this.killed_event = ewent();
            this.alive = true;
        }
        isPaused() {
            return false;
        }
        isAlive() {
            return this.alive;
        }
        revoke() {
            if (!this.alive)
                return;
            this.alive = false;
            this.killed_event.trigger(this);
            this.lifetime_manager.kill();
        }
        onKilled(f) {
            this.killed_event.on(f);
            return this;
        }
        isOneTime() {
            return false;
        }
        notify(value) {
            if (!this.alive)
                return;
            this._last_notification = value;
            try {
                this.handle(value);
            }
            catch (e) {
                if (e instanceof Error) {
                    console.error(e.stack);
                    log().log(e.toString(), "Capturing");
                }
            }
            if (this.isOneTime())
                this.revoke();
        }
        lastNotification() {
            return this._last_notification;
        }
        setupHandlers(service) {
            return [];
        }
        endLifetime() {
            this.revoke();
        }
    }
    AbstractCaptureService.InterestToken = InterestToken;
})(AbstractCaptureService || (AbstractCaptureService = {}));
export class DerivedCaptureService extends AbstractCaptureService {
    constructor() {
        super();
        this.sources = [];
    }
    addDataSource(s, options) {
        const self = this;
        const token = s.subscribe(new class extends AbstractCaptureService.InterestToken {
            handle(source_value) {
                if (self.sources.every(s => s.token.lastNotification()?.time?.tick == source_value.time.tick)) {
                    const interests = self.getInterests(source_value.time); // This looks like a recalculation, but getInterests caches the results for each tick
                    const value = self.raw_last_capture = {
                        time: source_value.time,
                        options: null,
                        value: self.processNotifications(interests)
                    };
                    interests.forEach(token => {
                        token.token.notify({
                            time: value.time,
                            options: token.options,
                            value: self.transformValueForNotification(token.options, value)
                        });
                    });
                }
            }
            options(time) {
                const interested_in_this_tick = self.getInterests(time);
                if (interested_in_this_tick.length == 0)
                    return null;
                const compound_options = options(time, interested_in_this_tick.map(t => t.options));
                return {
                    ...compound_options,
                    interval: FakeLodash.minBy([CaptureInterval.level(200), ...interested_in_this_tick.map(t => t.options.interval)], t => t.tick_modulo),
                    original_interests: interested_in_this_tick
                };
            }
            /*options(time: AbstractCaptureService.CaptureTime): SourceOptionsT {
              const interested_in_this_tick = self.interests
                .filter(t => !t.isPaused() && (time.tick % t.tickModulo() == 0))
                .map(t => t.options(time))
      
              return options(interested_in_this_tick)
            }*/
            isPaused() {
                return self.interests.every(t => t.isPaused());
            }
        });
        this.sources.push({
            service: s,
            token: token
        });
        return token;
    }
    endLifetime() {
        this.sources.forEach(t => t.token.revoke());
    }
}
export class CaptureInterval {
    constructor(level) {
        this.level = level;
        this.tick_modulo = Math.pow(2, level);
    }
    interval() {
        return this.tick_modulo * (1000 / CaptureInterval.global_capturing_fps);
    }
    matches(time) {
        return (time.tick % this.tick_modulo) == 0;
    }
    static level(level) {
        return new CaptureInterval(level);
    }
    static fromApproximateInterval(interval) {
        const tier = Math.max(0, Math.round(Math.log2(interval / (1000 / CaptureInterval.global_capturing_fps))));
        return new CaptureInterval(tier);
    }
    static setGlobalCapturingFps(fps) {
        this.global_capturing_fps = fps;
    }
    static globalCapturingFps() {
        return this.global_capturing_fps;
    }
}
CaptureInterval.global_capturing_fps = 60;
export var CaptureService;
(function (CaptureService) {
    CaptureService.MAX_FPS = 60;
    CaptureService.MIN_CAPTURE_INTERVAL = 1000 / CaptureService.MAX_FPS;
})(CaptureService || (CaptureService = {}));
